'use client'

import { useMemo, useState } from 'react'
import { cn } from '../ui-kitNew/cnNew'
import { Popover } from '../ui-kitNew/popoverNew'
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from '../ui-kitNew/iconsNew'

const pad = (n) => String(n).padStart(2, '0')

const toISO = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`

const parseISO = (iso) => {
  const [y, m, d] = String(iso).split('-').map(Number)
  return new Date(y, m - 1, d)
}

const formatISO = (iso) => {
  if (!iso) return ''
  const [y, m, d] = String(iso).split('-')
  return `${d}/${m}/${y}`
}

const WEEKDAYS = ['L', 'M', 'X', 'J', 'V', 'S', 'D']
const MONTHS = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
]

/** Construye las 42 celdas (6 semanas) de la vista, empezando por lunes. */
function buildMonthCells(year, month) {
  const first = new Date(year, month, 1)
  const offset = (first.getDay() + 6) % 7
  const start = new Date(year, month, 1 - offset)
  return Array.from({ length: 42 }, (_, i) => {
    const date = new Date(start)
    date.setDate(start.getDate() + i)
    return date
  })
}

/**
 * Selector de rango de fechas en un único campo: un solo calendario donde se
 * pulsa la fecha inicial y después la final. El valor es `[fromISO, toISO]`
 * con strings 'YYYY-MM-DD' (posiciones vacías como '').
 */
export function DateRangePicker({
  value,
  onChange,
  placeholder = 'Todas las fechas',
  align = 'start',
  className,
}) {
  const from = (value || [])[0] || ''
  const to = (value || [])[1] || ''

  const initialView = useMemo(() => {
    const base = from ? parseISO(from) : new Date()
    return { year: base.getFullYear(), month: base.getMonth() }
  }, [])

  const [view, setView] = useState(initialView)
  const [hoverISO, setHoverISO] = useState('')

  // Al abrir el calendario, muestra el mes de la selección actual (o actual).
  const handleOpenChange = (open) => {
    if (open) {
      const base = from ? parseISO(from) : new Date()
      setView({ year: base.getFullYear(), month: base.getMonth() })
      setHoverISO('')
    }
  }

  const pickDay = (date) => {
    const iso = toISO(date)
    if (!from || (from && to)) {
      onChange([iso, ''])
      return
    }
    // Segundo clic: ordena inicio/fin automáticamente.
    if (iso < from) onChange([iso, from])
    else onChange([from, iso])
  }

  // Rango efectivo mostrado (incluye previsualización al pasar el ratón).
  const previewTo =
    from && !to && hoverISO ? (hoverISO >= from ? hoverISO : from) : to
  const previewFrom =
    from && !to && hoverISO && hoverISO < from ? hoverISO : from

  const cells = useMemo(() => buildMonthCells(view.year, view.month), [view])

  const summary =
    from && to
      ? `${formatISO(from)} – ${formatISO(to)}`
      : from
        ? `${formatISO(from)} – …`
        : ''

  const moveMonth = (delta) =>
    setView((prev) => {
      const next = new Date(prev.year, prev.month + delta, 1)
      return { year: next.getFullYear(), month: next.getMonth() }
    })

  return (
    <Popover
      width={264}
      align={align}
      className="w-full"
      onOpenChange={handleOpenChange}
      renderTrigger={({ open, toggle, props }) => (
        <button
          type="button"
          onClick={toggle}
          {...props}
          className={cn(
            'flex w-full items-center gap-2 rounded-lg border bg-card px-[12px] text-left transition-colors',
            'h-[40px] text-[14px]',
            open ? 'border-ring' : 'border-input hover:border-ring/60',
            className
          )}
        >
          <CalendarGlyph />
          <span
            className={cn(
              'min-w-0 flex-1 truncate',
              summary ? 'text-foreground' : 'text-muted-foreground'
            )}
          >
            {summary || placeholder}
          </span>
          {summary && (
            <span
              role="button"
              tabIndex={0}
              aria-label="Limpiar fechas"
              onClick={(event) => {
                event.stopPropagation()
                onChange(['', ''])
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.stopPropagation()
                  onChange(['', ''])
                }
              }}
              className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <XIcon size={11} />
            </span>
          )}
        </button>
      )}
    >
      {({ close }) => (
        <div onMouseLeave={() => setHoverISO('')}>
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              aria-label="Mes anterior"
              onClick={() => moveMonth(-1)}
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <ChevronLeftIcon size={15} />
            </button>
            <span className="text-[13px] font-semibold capitalize text-foreground">
              {MONTHS[view.month]} {view.year}
            </span>
            <button
              type="button"
              aria-label="Mes siguiente"
              onClick={() => moveMonth(1)}
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <ChevronRightIcon size={15} />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-y-0.5">
            {WEEKDAYS.map((day) => (
              <span
                key={day}
                className="flex h-6 items-center justify-center text-[11px] font-semibold uppercase text-muted-foreground"
              >
                {day}
              </span>
            ))}
            {cells.map((date) => {
              const iso = toISO(date)
              const outside = date.getMonth() !== view.month
              const isStart = Boolean(previewFrom) && iso === previewFrom
              const isEnd = Boolean(previewTo) && iso === previewTo
              const inRange =
                previewFrom &&
                previewTo &&
                iso > previewFrom &&
                iso < previewTo

              return (
                <button
                  key={iso}
                  type="button"
                  disabled={outside}
                  onClick={() => pickDay(date)}
                  onMouseEnter={() => setHoverISO(iso)}
                  className={cn(
                    'mx-auto flex h-7 w-7 items-center justify-center rounded-md text-[12.5px] transition-colors',
                    outside
                      ? 'cursor-default text-transparent'
                      : 'text-foreground hover:bg-primary-soft',
                    inRange && 'rounded-none bg-primary-soft text-primary',
                    (isStart || isEnd) &&
                      'bg-primary font-semibold text-primary-foreground hover:bg-primary'
                  )}
                >
                  {outside ? '' : date.getDate()}
                </button>
              )
            })}
          </div>

          <div className="mt-3 border-t border-border pt-2.5">
            <div className="mb-2 text-[12px] text-muted-foreground">
              {!from
                ? 'Selecciona la fecha de inicio'
                : !to
                  ? 'Selecciona la fecha de fin'
                  : `${formatISO(from)} – ${formatISO(to)}`}
            </div>
            <button
              type="button"
              onClick={() => {
                onChange(['', ''])
                close()
              }}
              className="w-full rounded-lg border border-input py-1.5 text-[13px] font-medium text-foreground hover:bg-muted"
            >
              Limpiar
            </button>
          </div>
        </div>
      )}
    </Popover>
  )
}

function CalendarGlyph() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0 text-muted-foreground"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  )
}
